import React, { useEffect, useRef, useState } from 'react';

import ISOSearchBar from '../../TopbarContainer/Topbar/ISOSearchBar/ISOSearchBar';
import { HERO_IMAGE, TYPEWRITER_WORDS, HERO_SEARCH_PLACEHOLDER } from '../homeContent';
import { useLiveHorseCount } from '../horseCount';

import css from './Hero.module.css';

// Typewriter timing (ms) — unhurried, readable pace to match the live site.
const TYPE_SPEED = 280;
const DELETE_SPEED = 70;
const PAUSE_AFTER_TYPE = 5600;
const PAUSE_AFTER_DELETE = 600;

/**
 * Typewriter hook — types a word, pauses, deletes, advances to the next word,
 * then loops. Purely visual; no interaction. Returns the current substring.
 */
const useTypewriter = words => {
  const [text, setText] = useState('');
  const wordIndex = useRef(0);
  const phase = useRef('typing'); // 'typing' | 'deleting'
  const timer = useRef(null);

  useEffect(() => {
    if (!words || words.length === 0) return undefined;

    const tick = () => {
      const fullWord = words[wordIndex.current % words.length];

      if (phase.current === 'typing') {
        const next = fullWord.slice(0, text.length + 1);
        setText(next);
        if (next === fullWord) {
          phase.current = 'deleting';
          timer.current = setTimeout(tick, PAUSE_AFTER_TYPE);
        } else {
          timer.current = setTimeout(tick, TYPE_SPEED);
        }
      } else {
        const next = fullWord.slice(0, Math.max(0, text.length - 1));
        setText(next);
        if (next === '') {
          phase.current = 'typing';
          wordIndex.current = (wordIndex.current + 1) % words.length;
          timer.current = setTimeout(tick, PAUSE_AFTER_DELETE);
        } else {
          timer.current = setTimeout(tick, DELETE_SPEED);
        }
      }
    };

    timer.current = setTimeout(tick, TYPE_SPEED);
    return () => clearTimeout(timer.current);
    // Re-run the scheduler whenever `text` changes (drives each character step).
  }, [text, words]);

  return text;
};

/**
 * Homepage hero — full-bleed photo, maroon scrim, typewriter headline, the
 * shared keyword+ISO search, and a trust line.
 *
 * @param {Object} props
 * @param {Function} props.onSearchSubmit async ({ keywords }) => void — wired to
 *   the shared route-at-submit orchestration by the HomePage container.
 */
const Hero = props => {
  const { onSearchSubmit } = props;
  const word = useTypewriter(TYPEWRITER_WORDS);
  const horseCount = useLiveHorseCount();

  return (
    <section className={css.hero}>
      <div
        className={css.heroImage}
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        role="img"
        aria-label="A mare and foal grazing in an open pasture"
      />
      {/* Top scrim — darkens the top of the hero so the cream header reads
          cleanly over the photo. Topmost background layer, below content. */}
      <div className={css.topScrim} aria-hidden="true" />
      <div className={css.scrim} aria-hidden="true" />

      <div className={css.content}>
        <h1 className={css.headline}>
          <span className={css.headlinePrefix}>Find your</span>{' '}
          <span className={css.headlineSegment}>
            <span className={css.animatedWord}>{word}</span>
            <span className={css.caret} aria-hidden="true" />
            {' '}horse.
          </span>
        </h1>

        <div className={css.searchRow}>
          <ISOSearchBar
            variant="hero"
            onSearchSubmit={onSearchSubmit}
            placeholder={HERO_SEARCH_PLACEHOLDER}
            ariaLabel="Search horses"
          />
        </div>

        <p className={css.trustLine}>
          <span>Verified papers</span>
          <span className={css.dot} aria-hidden="true">
            ·
          </span>
          {/* Live horse-count stat — desktop-only: the mobile line must never
              wrap, and the punch-list dollar stat below takes the mobile slot. */}
          <span className={css.countStat}>
            {horseCount} horses
            <span className={css.andCounting}> and counting</span>
          </span>
          <span className={`${css.dot} ${css.countStat}`} aria-hidden="true">
            ·
          </span>
          {/* One text node serves both variants: desktop "Over $78 million in
              horses sold", mobile compact "Over $78 million sold" (infix hidden
              via .inHorses — same idiom as .andCounting). */}
          <span>
            Over $78 million<span className={css.inHorses}> in horses</span> sold
          </span>
        </p>
      </div>
    </section>
  );
};

export default Hero;
